import React from 'react';
import styled from 'styled-components';
import {
  TextField as BaseTextField,
  TextFieldProps as BaseTextFieldProps,
} from '@mui/material';
import { isNumeric } from '../../utils/common';
import { ComponentSize, COMPONENT_SIZE_MAP } from '../../types/types';

export type TextFieldProps = Omit<BaseTextFieldProps, 'size'> & {
  size?: ComponentSize;
  numeric?: boolean;
  readOnly?: boolean;
};

type TextFieldStyledProps = Omit<BaseTextFieldProps, 'size'> & {
  $size?: ComponentSize;
  $readOnly?: boolean;
};

const TextFieldStyled = styled(BaseTextField)<TextFieldStyledProps>`
  .MuiInputBase-root {
    height: ${({ $size, multiline }: TextFieldStyledProps) =>
      multiline ? 'fit-content' : `${COMPONENT_SIZE_MAP[$size || 'medium']}px`};
    min-height: ${({ $size }: TextFieldStyledProps) =>
      `${COMPONENT_SIZE_MAP[$size || 'medium']}px`};
    font-size: 14px;
    border-radius: 8px;
    padding-top: ${({ multiline }) => (multiline ? 10 : 0)}px;
    padding-bottom: ${({ multiline }) => (multiline ? 10 : 0)}px;
    padding-right: ${({ multiline }) => (multiline ? '0px' : undefined)};
    align-items: ${({ multiline }) => (multiline ? 'flex-start' : undefined)};

    &.Mui-disabled {
      background-color: ${({ theme }) => theme.palette.common.gray10};
      border: none;
      color: ${({ theme }) => theme.palette.common.gray7};
      .MuiOutlinedInput-notchedOutline {
        border: none !important;
      }
    }
  }

  .MuiOutlinedInput-root {
    border: 1px solid ${({ theme }) => theme.palette.common.gray7};
    ${({ theme, error }) =>
      `
        border: none;
        .MuiOutlinedInput-notchedOutline {
          border: 1px solid ${
            theme.palette.common[error ? 'red' : 'gray7']
          } !important;
          border-radius: 8px;
          transition: border 0.2s !important;
        };
        &.Mui-focused {
          border: none !important;
          .MuiOutlinedInput-notchedOutline {
            border: 1px solid ${theme.palette.common.black} !important;
          };
        };
        :hover {
          .MuiOutlinedInput-notchedOutline {
            border: 1px solid ${theme.palette.common.black} !important;
          }
        }
        &.Mui-disabled {
          .MuiOutlinedInput-notchedOutline {
            border: none !important;
          }
        }
      `};
  }

  .MuiOutlinedInput-root.Mui-focused {
    border: 1px solid ${({ theme }) => theme.palette.common.black};
  }

  .MuiOutlinedInput-notchedOutline {
    border: none !important;
    border-width: 0px !important;
  }

  & .MuiInputLabel-root {
    top: ${({ $size }: any) => ($size === 'large' ? '-6px' : '-11px')};
  }

  & .MuiInputLabel-shrink {
    top: 1px;
    font-size: 14px;
  }

  .MuiInputBase-input {
    &::placeholder {
      color: ${({ theme }) => theme.palette.common.slate};
      opacity: 1;
    }
  }

  .MuiFormHelperText-root {
    margin-left: 0;
  }

  pointer-events: ${({ $readOnly }) => ($readOnly ? 'none' : 'auto')};

  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active {
    box-shadow: 0 0 0 30px white inset !important;
    -webkit-box-shadow: 0 0 0 30px white inset !important;
  }
`;

const TextField = React.forwardRef(
  ({ size, numeric, readOnly, ...props }: TextFieldProps, ref: any) => {
    const isValidKey = (e: any) => {
      if (!numeric) return true;

      return (
        isNumeric(e.key) ||
        (e.metaKey && e.key.toLowerCase() === 'a') ||
        e.key === 'Backspace' ||
        e.key.toLowerCase().includes('arrow')
      );
    };

    const handleKeyDown = (e: any) => {
      if (!isValidKey(e) || readOnly) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      props?.onKeyDown?.(e);
    };

    return (
      <TextFieldStyled
        ref={ref}
        $size={size}
        $readOnly={readOnly}
        {...props}
        onKeyDown={handleKeyDown}
      />
    );
  },
);

export default TextField;
